import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  getActionLabel,
  IActionRepeaterValue,
  IActionType,
  isValidUrl,
  TableAdapter,
} from '@makyoapp/makyo-common'
import {
  ActionVerticalNodeAtom,
  Button,
  Field,
  RenderActionTypeIcon,
  SelectDropdown,
  SimpleInput,
} from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'

import { DialogSelectV2 } from '../Builder/DialogSelectV2'
import { MenuSelectV2 } from '../Builder/MenuSelectV2'
import { PageSelectV2 } from '../Builder/PageSelectV2'
import { TableDataSourceDropdown } from '../Builder/TableDataSourceDropdown'
import { RenderColumn } from '../RenderColumn'

export function VariableSimpleAction({
  id,
  value,
  onDone,
  actionTypes,
  excludeActions = [],
}: {
  id: string
  value?: Omit<IActionRepeaterValue, 'id'>
  onDone: (value?: Omit<IActionRepeaterValue, 'id'>) => void
  actionTypes?: IActionType[]
  excludeActions?: IActionType[]
}) {
  //const [tempValue, setTempValue] = useState<ActionType | undefined>(
  //  value?.type
  //)
  //const [tempSetting, setTempSetting] = useState(value?.settings ?? {})
  const [tempValue, setTempValue] = useState(value)
  return (
    <div className='flex flex-col gap-2'>
      <VariableSimpleActionBody
        id={id}
        //setTempSetting={setTempSetting}
        //setTempValue={setTempValue}
        //tempSetting={tempSetting}
        //tempValue={tempValue}
        value={tempValue}
        onChange={(next) => {
          setTempValue(next)
        }}
        actionTypes={actionTypes}
        excludeActions={excludeActions}
      />
      <VariableSimpleActionButton
        value={tempValue}
        onClick={() => {
          onDone(tempValue)
        }}
      >
        <Trans>Select</Trans>
      </VariableSimpleActionButton>
    </div>
  )
}

export function VariableSimpleActionButton({
  className,
  value,
  onClick,
  children,
}: {
  className?: string
  value?: Omit<IActionRepeaterValue, 'id'>
  onClick: () => void
  children?: React.ReactNode
}) {
  return (
    <Button
      className={className}
      disabled={
        ([
          IActionType.GoToScreen,
          IActionType.OpenNavDrawer,
          IActionType.OpenMenu,
          IActionType.ShowContextualMenu,
          IActionType.OpenLink,
          IActionType.OpenMap,
          IActionType.ShowDialog,
          IActionType.ShowSnackbar,
          IActionType.AddNewRow,
          // IActionType.AddRecordForm,
        ].includes(value?.type as unknown as IActionType) &&
          !value?.settings?.value) ||
        (value?.type === IActionType.OpenLink && !isValidUrl(value?.settings?.value?.toString() ?? ''))
      }
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

function VariableSimpleActionToggle({
  id,
  table,
  showLabel,
  value,
  onChange,
}: {
  onChange: (next: { column?: string; userSpecific?: boolean }) => void
  value?: {
    column?: string
    userSpecific?: boolean
  }
  id?: string
  table: TableAdapter
  showLabel?: boolean
}) {
  const { tables } = useDbClientContext()
  const colOptions = useMemo(() => {
    return [
      { id: '', name: 'Select Column' } as ColumnAdapter,
      ...table.columns.filter((c) => {
        /*if (c.type === ColumnTypes.Lookup) {
          const connCol = table.columns.find((cc) => {
            return cc.id === c?.lookup?.connColId
          })
          if (connCol) {
            const connStg = connCol.connections
            const foreignTable = tables.find(
              (tb) => tb.id === connStg?.fTableId
            )
            const foreignColumn = foreignTable?.columns.find(
              (fc) => fc.id === c?.lookup?.colId
            )
            return foreignColumn && foreignColumn.type === ColumnTypes.Boolean
          } else {
            return false
          }
        }*/
        return c.type === ColumnTypes.Boolean
      }),
    ]
  }, [table])

  return (
    <>
      <Wrap label={<Trans>Column</Trans>} showLabel={showLabel}>
        <SelectDropdown
          withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
          id={`${id}-column`}
          value={colOptions.find(({ id }) => id === value?.column)}
          onChange={async (opt) => {
            const val = opt.id
            onChange({
              column: val,
              userSpecific: value?.userSpecific === undefined || value.userSpecific,
            })
          }}
          options={colOptions}
          optionLabel={(opt) => `${(opt as ColumnAdapter).id} ${(opt as ColumnAdapter).name}`}
          renderOption={(opt) => (
            <RenderColumn
              withStyle={false}
              col={opt as ColumnAdapter}
              className={clsx((opt as ColumnAdapter)?.id === '' && 'text-light')}
            />
          )}
        />
      </Wrap>
      {/* <Field>
        <label>
          <Trans>User Specific</Trans>
        </label>
        <Switch
          id={`${id}-user-specific`}
          checked={value && value.userSpecific !== undefined ? value.userSpecific : true}
          onClick={(next) => {
            onChange({
              ...(value || { column: '' }),
              userSpecific: next,
            })
          }}
        />
      </Field> */}
    </>
  )
}

const Wrap = ({
  label,
  children,
  showLabel,
  isVertical = true,
}: {
  label: React.ReactNode
  children: React.ReactNode
  showLabel?: boolean
  isVertical?: boolean
}) => {
  if (showLabel) {
    return (
      <Field isVertical={isVertical}>
        <label>{label}</label>
        {children}
      </Field>
    )
  } else {
    return <>{children}</>
  }
}

export function VariableSimpleActionBody({
  id,
  value,
  portal = true,
  //tempValue,
  //setTempValue,
  //tempSetting,
  //setTempSetting,
  onChange,
  actionTypes,
  excludeActions = [],
  excludeDialogs,
  excludeMenus,
  excludePages,
  showLabel,
  tableSource,
}: {
  id: string
  value?: Omit<IActionRepeaterValue, 'id'>
  onChange: (value?: Omit<IActionRepeaterValue, 'id'>) => void
  actionTypes?: IActionType[]
  excludeActions?: IActionType[]
  excludeDialogs?: string[]
  excludeMenus?: string[]
  excludePages?: string[]
  portal?: boolean
  showLabel?: boolean
  tableSource?: TableAdapter
}) {
  const { t } = useTranslation()
  const { tables } = useDbClientContext()

  const options = useMemo(() => {
    return actionTypes
      ? [...actionTypes]
      : [
          IActionType.GoBack,
          IActionType.PrevItem,
          IActionType.NextItem,
          IActionType.GoToScreen,
          IActionType.RefreshScreen,
          IActionType.OpenNavDrawer,
          IActionType.Logout,
          IActionType.OpenLink,
          IActionType.OpenMap,
          IActionType.ShowDialog,
          IActionType.ShowSnackbar,
          IActionType.CloseAlert,
          IActionType.AddNewRow,
          IActionType.UpdateRow,
          IActionType.DeleteRow,
          IActionType.IncrementValue,
          IActionType.ShowDetailsScreen,
          IActionType.ShowContextualMenu,
          IActionType.OpenMenu,
          IActionType.ToggleValue,
        ].filter((a) => {
          return !excludeActions.includes(a as IActionType)
        })
  }, [actionTypes, excludeActions])

  const [error, setError] = useState('')

  // TODO refactor this later, let consumer declare what component action picker they need to use
  const isOnlySingleOption = options.length === 1

  return (
    <>
      <Wrap label={<Trans>Action</Trans>} showLabel={showLabel}>
        {isOnlySingleOption ? (
          // UN-EDITABLE SINGLE ACTION PICKER
          <ActionVerticalNodeAtom
            onEdit={() => {}}
            onDelete={() => {}}
            actionName={options[0].toString()}
            actionDescription={''}
            actionId={id}
            listener={{}}
            hideEdit
            hideDelete
            hideSort
          />
        ) : (
          <SelectDropdown
            id={`${id}-action-type-dd`}
            portalMenu={portal}
            menuFullWidth={!portal}
            menuZIndex='z-20'
            menuContainerClassName={!portal ? '' : 'mk-ag-sdd'}
            value={value?.type}
            onChange={(opt, e) => {
              e.stopPropagation()
              const nextValue = {
                ...(value || {}),
              }
              if (opt !== nextValue?.type) {
                nextValue.settings = {}
                setError('')
              }
              nextValue.type = opt as IActionType
              onChange(nextValue as Omit<IActionRepeaterValue, 'id'>)
            }}
            options={options}
            renderOption={(opt) => {
              return (
                <div className='flex-c w-full'>
                  <RenderActionTypeIcon type={opt as IActionType} />
                  <Trans>{getActionLabel(opt as IActionType)}</Trans>
                </div>
              )
            }}
          />
        )}
      </Wrap>
      {value?.type && [IActionType.OpenMenu, IActionType.ShowContextualMenu].includes(value?.type) && (
        <Wrap label={<Trans>Menu</Trans>} showLabel={showLabel}>
          <MenuSelectV2
            id={`${id}-menu`}
            portal={portal}
            excludes={excludeMenus}
            value={value?.settings?.value as string}
            onChange={(menu) => {
              const prev = value || { settings: {} }
              onChange({
                ...prev,
                settings: {
                  ...prev.settings,
                  value: menu,
                },
              })
            }}
          />
        </Wrap>
      )}

      {/* {value?.type && [IActionType.ShowContextualMenu].includes(value?.type) && (
        <Wrap label={<Trans>Position</Trans>} showLabel={showLabel}>
          <SelectDropdown
            id={`${id}-position`}
            portalMenu={portal}
            menuFullWidth={!portal}
            menuZIndex='z-20'
            menuContainerClassName={!portal ? '' : 'mk-ag-sdd'}
            value={value?.settings?.pos as string}
            options={['Top Bar', 'Bottom Bar']}
            renderOption={(opt) => <Trans>{opt}</Trans>}
            onChange={(pos) => {
              const prev = value || { settings: {} }
              onChange({
                ...prev,
                settings: {
                  ...prev.settings,
                  pos,
                },
              })
            }}
          />
        </Wrap>
      )} */}

      {value?.type === IActionType.ShowDialog && (
        <Wrap label={<Trans>Dialog</Trans>} showLabel={showLabel}>
          <DialogSelectV2
            excludes={excludeDialogs}
            portal={portal}
            id={`${id}-dialog`}
            value={value?.settings?.value as string}
            onChange={(dialog) => {
              onChange({
                ...(value || {}),
                settings: {
                  value: dialog,
                },
              })
            }}
          />
        </Wrap>
      )}
      {(value?.type === IActionType.GoToScreen || value?.type === IActionType.OpenNavDrawer) && (
        <Wrap label={<Trans>Screen</Trans>} showLabel={showLabel}>
          <PageSelectV2
            id={`${id}-page`}
            portal={portal}
            excludes={excludePages}
            value={value?.settings?.value as string}
            onChange={(page) => {
              onChange({
                ...(value || {}),
                settings: {
                  value: page,
                },
              })
            }}
          />
        </Wrap>
      )}
      {value?.type === IActionType.AddNewRow && (
        <Wrap label={<Trans>Table</Trans>} showLabel={showLabel}>
          <TableDataSourceDropdown
            tables={tables}
            value={value.settings.value as string}
            onChange={(table) => {
              onChange({
                ...(value || {}),
                settings: {
                  value: table ?? '',
                },
              })
            }}
          />
        </Wrap>
      )}
      {[IActionType.ToggleValue].includes(value?.type as IActionType) && tableSource && (
        <VariableSimpleActionToggle
          onChange={(next) => {
            onChange({
              type: IActionType.ToggleValue,
              ...(value || {}),
              settings: {
                ...next,
              },
            })
          }}
          id={`${id}-toggle`}
          table={tableSource}
          value={
            value?.settings as {
              column: string
              userSpecific: boolean
            }
          }
          showLabel={showLabel}
        />
      )}
      {[IActionType.OpenLink, IActionType.OpenMap, IActionType.ShowSnackbar].includes(value?.type as IActionType) && (
        <SimpleInput
          type='text'
          placeholder={(() => {
            switch (value?.type) {
              case IActionType.OpenLink:
                return t('Enter URL')
              case IActionType.OpenMap:
                return t('Enter Address')
              default:
                return t('Enter Text')
            }
          })()}
          error={error}
          value={`${value?.settings?.value ?? ''}`}
          onChange={(e) => {
            const val = e.currentTarget.value
            if (value?.type === IActionType.OpenLink) {
              setError(!isValidUrl(val) ? 'Invalid URL' : '')
            }
            onChange({
              ...(value || {}),
              settings: {
                value: val,
              },
            } as Omit<IActionRepeaterValue, 'id'>)
          }}
        />
      )}
    </>
  )
}
